import { isAxiosError } from "axios";
import {
  Checkpoint,
  ClientApp,
  TableDetails,
  TableSourceJS,
} from "target_wasm";
import { isImportedSource } from "components/canvas/utils/roles";

interface ReleaseLabel {
  tooltip: string;
  accent: "accent" | "success" | "warning";
}

export const getReleaseLabel = (
  releasedVersion: number | bigint | null | undefined,
  currentVersion: number | bigint | null | undefined,
  isImported = false,
): ReleaseLabel | null => {
  if (releasedVersion == null || currentVersion == null) return null;
  const diff = BigInt(currentVersion) - BigInt(releasedVersion);
  if (diff === 0n) {
    return {
      // An importer only ever shows a release, so it cannot move off it.
      tooltip: isImported
        ? "Latest release"
        : "Current version is the released version",
      accent: "accent",
    };
  }
  const n = diff > 0n ? diff : -diff;
  return {
    tooltip: `${n} change${n > 1n ? "s" : ""} ${diff > 0n ? "ahead of" : "behind"} released version`,
    accent: diff > 0n ? "success" : "warning",
  };
};

export interface VersionInfo {
  isRestored: boolean;
  isRestoreDisabled: boolean;
  restoreTooltip: string;
}

export const getVersionInfo = (
  checkpointVersion: bigint,
  tableDetails: TableDetails | null,
  releasedVersions?: number[],
): VersionInfo => {
  if (!tableDetails)
    return { isRestored: false, isRestoreDisabled: true, restoreTooltip: "" };
  const isRestored = tableDetails.current_is_restored === checkpointVersion;
  // An importer checks out only a release; open until the release history loads.
  const checkoutBlocked =
    tableDetails.source === TableSourceJS.Imported &&
    releasedVersions !== undefined &&
    !releasedVersions.includes(Number(checkpointVersion));
  return {
    isRestored,
    isRestoreDisabled:
      checkpointVersion === tableDetails.version ||
      isRestored ||
      checkoutBlocked ||
      // An app's table never goes back, whatever its mode (docs/Apps.md).
      tableDetails.source === TableSourceJS.App,
    restoreTooltip: isImportedSource(tableDetails.source)
      ? "Check out this checkpoint"
      : "Restore this checkpoint",
  };
};

const isAutoTempCheckpoint = (checkpoint: Checkpoint): boolean =>
  checkpoint.created_by_email() === "system" &&
  checkpoint.name_js().startsWith("temp-checkpoint-");

export const cleanupOrphanTempCheckpoints = (
  clientApp: ClientApp | null | undefined,
  checkpointsByTable: Record<string, Checkpoint[]>,
): void => {
  if (!clientApp) return;
  for (const [tableId, cps] of Object.entries(checkpointsByTable)) {
    // An imported table's checkpoints are its parent's, and the server refuses them.
    if (!clientApp.peek_table_permissions(tableId)?.can_checkpoint) continue;
    for (const cp of cps) {
      if (!isAutoTempCheckpoint(cp)) continue;
      try {
        if (clientApp.count_dependent_diff_views(tableId, cp.id_str()) === 0)
          clientApp.request_checkpoint_removal(tableId, cp.id_str());
      } catch {
        /* next time */
      }
    }
  }
};

const CANT_UNRELEASE_CATALOG =
  "Can't unrelease: the catalog serves this release. Publish a new dataset release instead.";

const CANT_UNRELEASE: Record<string, string> = {
  release_imported:
    "Can't unrelease: other models import this table. Release another checkpoint instead.",
  release_published: CANT_UNRELEASE_CATALOG,
  release_via_dataset_only: CANT_UNRELEASE_CATALOG,
};

/** A refusal's status and code: a 409 names it `error`, the release gate's 403 `error_code`. */
const refusalOf = (err: unknown): [number | undefined, string | undefined] => {
  const response = isAxiosError(err) ? err.response : undefined;
  return [
    response?.status,
    response?.data?.error ?? response?.data?.error_code,
  ];
};

/** A refused unrelease, said from the server's code, never its text. */
export const unreleaseRefusal = (err: unknown): string => {
  const [status, code] = refusalOf(err);
  return (
    (code && CANT_UNRELEASE[code]) ||
    (status === 403
      ? "Can't unrelease: you don't have permission."
      : "Couldn't unrelease. Please try again.")
  );
};

const CANT_RELEASE: Record<string, string> = {
  release_via_dataset_only:
    "Can't release: a catalog table is released with its dataset.",
};

/** A refused release, said from the server's code, never its text. */
export const releaseRefusal = (err: unknown): string => {
  const [status, code] = refusalOf(err);
  return (
    (code && CANT_RELEASE[code]) ||
    (status === 403
      ? "Can't release: you don't have permission."
      : status === 429
        ? "Can't release right now: the table is busy. Try again in a moment."
        : "Couldn't release. Please try again.")
  );
};

/** Keyed on the actor's `ActionAckFailure`. */
const CANT_DELETE: Record<string, string> = {
  ReleaseImported: "Can't delete: other models import this release.",
  ReleaseInCatalog:
    "Can't delete: the catalog serves this release. Publish a new dataset release first.",
  PermissionRequestFailed: "Can't delete: you don't have permission.",
};

/** A refused checkpoint delete, by the reason the client was sent. */
export const checkpointDeleteRefusal = (reason?: string | null): string =>
  (reason && CANT_DELETE[reason]) ||
  "Couldn't delete the checkpoint. Please try again.";

// The refusal ack names no checkpoint: a panel with a delete in flight takes it.
const deleteRefusalListeners = new Set<() => void>();

export const onCheckpointDeleteRefused = (listener: () => void) => {
  deleteRefusalListeners.add(listener);
  return () => void deleteRefusalListeners.delete(listener);
};

export const checkpointDeleteRefused = () =>
  deleteRefusalListeners.forEach((listener) => listener());

export const checkpointErrorMessage = (
  err: unknown,
  fallback: string,
): string => {
  const status = isAxiosError(err) ? err.response?.status : undefined;
  if (!status || status >= 500) return fallback;
  const data = isAxiosError(err) ? err.response?.data : undefined;
  const reason: string =
    data?.message ||
    data?.error ||
    (typeof data === "string" ? data : "") ||
    (err instanceof Error ? err.message : "") ||
    fallback;
  return reason
    .replace(/^(Bad Request|Conflict|Not Found):\s*/i, "")
    .replace(/^Invalid alias '[^']+':\s*/i, "");
};

const DAY_LABEL_GAP = 3;

/** Which points carry their date: a day's first, {@link DAY_LABEL_GAP} steps clear of other labels. */
export const dayLabels = (
  dates: readonly string[],
  reserved: readonly number[] = [dates.length - 1],
): number[] => {
  const out: number[] = [];
  dates.forEach((date, k) => {
    const prev = out[out.length - 1];
    const dayStarts = k === 0 || date !== dates[k - 1];
    if (
      dayStarts &&
      (prev === undefined || k - prev >= DAY_LABEL_GAP) &&
      reserved.every((r) => r < 0 || Math.abs(k - r) >= DAY_LABEL_GAP)
    )
      out.push(k);
  });
  return out;
};

export interface PointMonth {
  /** Sortable, "2026-03". */
  key: string;
  /** "Mar". */
  label: string;
  /** "March 2026". */
  title: string;
}

/** A folded month: a bar on the line, `from`–`to` as a % of the strip. */
interface TimelineMonth extends PointMonth {
  ids: string[];
  from: number;
  to: number;
}

/** An open month, or the month still going on, from its head (`chip` if one) to its last point. */
interface TimelineBand extends PointMonth {
  from: number;
  to: number;
  open: boolean;
  chip: boolean;
}

interface TimelineLayout {
  /** Each shown point's place, as a % of the strip; a point folded into its month has none. */
  at: Map<string, number>;
  months: TimelineMonth[];
  /** Each open month's chip, ahead of its first point, while months fold. */
  folds: Map<string, { from: number; to: number }>;
  bands: TimelineBand[];
  /** How many steps the strip spans, point to point. */
  steps: number;
}

const monthWidth = (count: number) => Math.min(3, Math.max(1.5, count / 6));
const OPEN_MONTH_WIDTH = 1.25;
/** The month still going on keeps this many of its newest by latest; a past month folds whole. */
export const FOLD_MIN = 5;

/** Where each point sits, one step apart: with `open` set, every other month folds into a
 *  chip, and the month still going on keeps its newest {@link FOLD_MIN} by latest. */
export function timelineLayout(
  points: readonly { id: string; month?: PointMonth }[],
  open: ReadonlySet<string> | null,
  tail: string | null = null,
): TimelineLayout {
  type Item =
    | { point: string; month?: PointMonth }
    | { fold: PointMonth }
    | { folded: PointMonth & { ids: string[] } };
  const size = new Map<string, number>();
  for (const p of points)
    if (p.month) size.set(p.month.key, (size.get(p.month.key) ?? 0) + 1);
  const seen = new Map<string, number>();
  const items: Item[] = [];
  let run: string | undefined;
  for (const p of points) {
    const key = p.month?.key;
    const n = key ? (size.get(key) ?? 0) : 0;
    const k = key ? (seen.get(key) ?? 0) : 0;
    if (key) seen.set(key, k + 1);
    const shown =
      open === null ||
      !key ||
      open.has(key) ||
      (key === tail && k >= n - FOLD_MIN);
    if (shown || !p.month) {
      if (
        p.month &&
        open?.has(p.month.key) &&
        (key !== tail || n > FOLD_MIN) &&
        key !== run
      )
        items.push({ fold: p.month });
      run = key;
      items.push({ point: p.id, month: p.month });
      continue;
    }
    run = undefined;
    const last = items[items.length - 1];
    if (last && "folded" in last && last.folded.key === key)
      last.folded.ids.push(p.id);
    else items.push({ folded: { ...p.month, ids: [p.id] } });
  }
  let u = 0;
  const spans = items.map((item, i) => {
    if (i > 0) u += 1;
    const from = u;
    if ("folded" in item) u += monthWidth(item.folded.ids.length);
    else if ("fold" in item) u += OPEN_MONTH_WIDTH;
    return [from, u] as const;
  });
  const steps = u;
  const pct = (x: number) => (steps === 0 ? 0 : (x / steps) * 100);
  const at = new Map<string, number>();
  const months: TimelineMonth[] = [];
  const folds = new Map<string, { from: number; to: number }>();
  const bands: TimelineBand[] = [];
  let head: Omit<TimelineBand, "to"> | null = null;
  items.forEach((item, i) => {
    const [from, to] = spans[i];
    const x = pct(from);
    if ("fold" in item) {
      folds.set(item.fold.key, { from: x, to: pct(to) });
      head = { ...item.fold, from: x, open: true, chip: true };
      return;
    }
    if ("folded" in item) {
      const { ids, ...month } = item.folded;
      months.push({ ...month, ids, from: x, to: pct(to) });
      head =
        month.key === tail
          ? { ...month, from: x, open: false, chip: true }
          : null;
      return;
    }
    at.set(item.point, x);
    if (open === null) return;
    const month = item.month;
    const band = bands.find((b) => b.key === (month?.key ?? tail));
    if (band) band.to = x;
    else if (month && head?.key === month.key) bands.push({ ...head, to: x });
    else if (month?.key === tail)
      bands.push({ ...month, from: x, to: x, open: false, chip: false });
  });
  return { at, months, folds, bands, steps };
}

/** Where the view goes when `id` is deleted: the next point in its month, else the one before. */
export const neighbourOf = (
  points: readonly { id: string; month?: PointMonth }[],
  id: string,
): string | undefined => {
  const k = points.findIndex((p) => p.id === id);
  if (k < 0) return undefined;
  const next = points[k + 1];
  const sameMonth =
    !!next && (!next.month || next.month.key === points[k].month?.key);
  return (sameMonth ? next : (points[k - 1] ?? next))?.id;
};

/** The month `d` falls in; only the month word localizes, as in lib/dates. */
export const monthOf = (d: Date): PointMonth => ({
  key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
  label: d.toLocaleDateString(undefined, { month: "short" }),
  title: `${d.toLocaleDateString(undefined, { month: "long" })} ${d.getFullYear()}`,
});

export const hoverProps = (on: (yes: boolean) => void) => ({
  onMouseEnter: () => on(true),
  onMouseLeave: () => on(false),
  onFocus: () => on(true),
  onBlur: () => on(false),
});
